import React, { useState } from "react";
import { useProjectAttachments } from "../../../data/project-attachments";
import { CreateAttachmentDialog } from "./create-attachment-dialog";
import { Button } from "../../../components/ui/button";
import { PlusIcon, XIcon } from "lucide-react";
import { useAttachmentDisplay } from "../../../lib/attachments/attachments";
import { useRemoveProjectAttachmentMutation } from "../../../data/mutations/remove-project-attachment";
import ConfirmationDialog from "../../../components/modals/confirmation-dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { FilePreview, isFileAttachment } from "./attachment-list";
import { HttpAttachment } from "tekkr-common/dist/model/attachments";

interface Props {
    target: string[];
    label: string;
    dialogTitle: string;
}

export function LinkedDocumentField({ target, label, dialogTitle }: Props) {
    const { getAttachments } = useProjectAttachments();
    const [dialogOpen, setDialogOpen] = useState(false);

    const file = getAttachments(target)[0] ?? null;

    return (
        <div className="flex flex-row items-center gap-2">
            <div className="text-sm font-medium text-muted-foreground">{label}</div>
            {file ? (
                <FileDisplay file={file} />
            ) : (
                <CreateAttachmentDialog
                    target={target}
                    title={dialogTitle}
                    showPickExisting
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                >
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setDialogOpen(true)}>
                        <PlusIcon className="me-1 h-3.5 w-3.5" /> Add
                    </Button>
                </CreateAttachmentDialog>
            )}
        </div>
    );
}

function FileDisplay({ file }: { file: HttpAttachment }) {
    const [showPreview, setShowPreview] = useState(false);
    const removeMutation = useRemoveProjectAttachmentMutation();
    const { icon: Icon, url } = useAttachmentDisplay(file);
    const isFile = isFileAttachment(file);

    return (
        <>
            {showPreview && isFile && <FilePreview attachment={file} onClose={() => setShowPreview(false)} />}
            <div className="flex items-center gap-1 rounded-md bg-accent px-2 py-1">
                <div
                    className="flex cursor-pointer items-center gap-1.5 hover:underline"
                    onClick={() => (isFile ? setShowPreview(true) : url && window.open(url, "_blank", "noreferrer"))}
                >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="max-w-[320px] truncate text-sm font-medium" title={file.name}>
                        {file.name}
                    </span>
                </div>
                <ConfirmationDialog
                    title="Remove File"
                    body="Are you sure you want to remove this file?"
                    confirm={() => removeMutation.mutateAsync({ attachmentId: file.id })}
                >
                    <DialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                            <XIcon className="h-3.5 w-3.5" />
                        </Button>
                    </DialogTrigger>
                </ConfirmationDialog>
            </div>
        </>
    );
}
